import React, { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';

const getNiceYAxisConfig = maxValue => {
  if (maxValue <= 0) {
    return { max: 5, interval: 1 };
  }

  const targetSplitNumber = 5;
  const interval = Math.max(1, Math.ceil(maxValue / targetSplitNumber));
  const max = (Math.floor(maxValue / interval) + 1) * interval;

  return { max, interval };
};

export default function SuccessRateBarChart({ barChartData }) {
  const chartRef = useRef(null);

  const safeData = Array.isArray(barChartData) ? barChartData : [];
  const labels = useMemo(() => safeData.map(d => d?.[0] || ''), [safeData]);
  const values = useMemo(() => safeData.map(d => Number(d?.[1] ?? 0)), [safeData]);
  const maxCount = useMemo(() => (values.length > 0 ? Math.max(...values) : 0), [values]);
  const yAxisConfig = useMemo(() => getNiceYAxisConfig(maxCount), [maxCount]);

  const option = useMemo(() => {
    return {
      grid: {
        left: 56,
        right: 20,
        top: 26,
        bottom: 46,
        show: true,
        borderColor: '#C7C7CC',
        borderWidth: 1,
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'solid',
            color: '#C7C7CC',
            width: 1,
          },
        },
        axisLabel: {
          color: '#8E8E93',
          fontSize: 12,
          margin: 12,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: yAxisConfig.max,
        interval: yAxisConfig.interval,
        minInterval: 1,
        boundaryGap: ['0%', '30%'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#8E8E93', margin: 14 },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#dcdfe6',
          },
        },
      },
      series: [
        {
          type: 'bar',
          data: values,
          barMaxWidth: 60,
          itemStyle: { color: '#4B68CF' },
        },
      ],
      tooltip: { trigger: 'axis' },
    };
  }, [labels, values, yAxisConfig]);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    const chart = echarts.init(el);
    chart.setOption(option);

    const resize = () => chart.resize();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      chart.dispose();
    };
  }, [option]);

  return <div ref={chartRef} style={{ height: 260, width: '100%' }} />;
}
